import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Play, Pencil, Trash2 } from 'lucide-react';
import PageLayout from '@components/layout/PageLayout';
import ListPageHeader from '@components/common/ListPageHeader';
import { FilterButtons } from '@components/common/FilterButtons';
import Button from '@components/common/Button';
import ConfirmDialog from '@components/common/ConfirmDialog';
import { RZTable } from '@components/common/table/RZTable';
import { QueryTypeBadge } from '@components/features/queries';
import { useQueryList } from '@hooks/useQueryList';
import { usePermissions } from '@contexts/AuthContext';
import { QUERY_PAGE_SIZES } from '@app-types';
import type { TableColumn, QueryListItem, QueryListTypeFilter, QueryListSortField, ListQueriesParams } from '@app-types';
import { formatInstantAsDate } from '@utils/dateUtils';
import styles from './QueriesPage.module.css';

const FILTER_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'personal', label: 'Personal' },
  { key: 'global', label: 'Global' }
] as const;

// Only these columns can be sorted server-side (US-02). Type/creator are display-only.
const SORTABLE_FIELDS: QueryListSortField[] = ['name', 'createdAt', 'updatedAt'];

const COLUMNS: TableColumn[] = [
  { key: 'name', label: 'Name', defaultVisible: true, sortable: true },
  { key: 'type', label: 'Type', defaultVisible: true },
  { key: 'creatorName', label: 'Created By', defaultVisible: true },
  { key: 'createdAt', label: 'Date Created', defaultVisible: true, sortable: true },
  { key: 'updatedAt', label: 'Last Modified', defaultVisible: true, sortable: true },
  { key: 'actions', label: 'Actions', defaultVisible: true, centered: true, width: '160px' }
];

const formatDate = (iso: string): string => formatInstantAsDate(iso);

const QueriesPage: React.FC = () => {
  const navigate = useNavigate();
  const permissions = usePermissions();

  const [tab, setTab] = useState<QueryListTypeFilter>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<QueryListSortField>('updatedAt');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(QUERY_PAGE_SIZES[0]);
  const [toDelete, setToDelete] = useState<QueryListItem | null>(null);

  // Debounce the search box so we don't fire a request on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const params: ListQueriesParams = useMemo(
    () => ({
      type: tab,
      search: search.trim() || undefined,
      sortBy: sortField,
      sortDir: sortDirection === 'ASC' ? 'asc' : 'desc',
      page,
      pageSize
    }),
    [tab, search, sortField, sortDirection, page, pageSize]
  );

  const { queries, total, counts, isLoading, deleteQuery, isDeleting } = useQueryList(params);

  const canModify = (query: QueryListItem): boolean => (query.type === 'global' ? permissions.isAdmin : true); // list only returns the user's own personal queries

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleSort = (columnKey: string): void => {
    if (!SORTABLE_FIELDS.includes(columnKey as QueryListSortField)) return;
    if (columnKey === sortField) {
      setSortDirection((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortField(columnKey as QueryListSortField);
      setSortDirection('ASC');
    }
    setPage(1);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!toDelete) return;
    await deleteQuery(toDelete.id);
    setToDelete(null);
  };

  const renderCell = (column: TableColumn, query: QueryListItem): React.ReactNode => {
    switch (column.key) {
      case 'name':
        return (
          <button className={styles.nameLink} onClick={() => navigate(`/queries/${query.id}/results`)}>
            {query.name}
          </button>
        );
      case 'type':
        return <QueryTypeBadge type={query.type} />;
      case 'creatorName':
        return query.creatorName;
      case 'createdAt':
        return formatDate(query.createdAt);
      case 'updatedAt':
        return formatDate(query.updatedAt);
      case 'actions':
        return (
          <div className={styles.rowActions}>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => navigate(`/queries/${query.id}/results`)}
              title="Execute"
              aria-label={`Execute query ${query.name}`}
            >
              <Play size={16} />
            </Button>
            {canModify(query) && (
              <Button
                size="xs"
                variant="ghost"
                onClick={() => navigate(`/queries/${query.id}/edit`, { state: { returnTo: '/queries' } })}
                title="Edit"
                aria-label={`Edit query ${query.name}`}
              >
                <Pencil size={16} />
              </Button>
            )}
            {canModify(query) && (
              <Button
                size="xs"
                variant="ghost"
                className={styles.removeButton}
                onClick={() => setToDelete(query)}
                title="Delete"
                aria-label={`Delete query ${query.name}`}
              >
                <Trash2 size={16} />
              </Button>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <PageLayout>
      <ListPageHeader
        icon={<Search size={24} />}
        title="Queries"
        count={counts.all}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Search by name..."
        additionalContent={
          <Button variant="primary" size="sm" onClick={() => navigate('/queries/new')}>
            <Plus size={16} />
            Create Query
          </Button>
        }
      />

      <FilterButtons
        filters={FILTER_OPTIONS}
        activeFilter={tab}
        onFilterChange={(key) => {
          setTab(key as QueryListTypeFilter);
          setPage(1);
        }}
        filterCounts={{ all: counts.all, personal: counts.personal, global: counts.global }}
      />

      <RZTable<QueryListItem>
        data={queries}
        columns={COLUMNS}
        getRowKey={(q) => q.id}
        renderCell={renderCell}
        sorting={{
          sortConfig: { field: sortField, direction: sortDirection },
          onSort: handleSort,
          isSorted: (columnKey) => columnKey === sortField
        }}
        pagination={{
          currentPage: page,
          totalPages,
          totalItems: total,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPage(1);
          },
          itemName: 'queries',
          pageSizeOptions: [...QUERY_PAGE_SIZES]
        }}
        state={{
          isLoading,
          isEmpty: total === 0,
          emptyTitle: 'No queries yet',
          emptyDescription: 'Create your first query to extract custom data.'
        }}
      />

      <ConfirmDialog
        isOpen={!!toDelete}
        title="Delete Query"
        message="Are you sure you want to permanently delete this query?"
        confirmText={isDeleting ? 'Deleting...' : 'Delete'}
        confirmVariant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </PageLayout>
  );
};

export default QueriesPage;
